import React, { useState, useEffect } from 'react';
import { useContributions, getMonthlyBreakdown, getDistrictBreakdown, deleteContribution } from '../../hooks/useContributions';
import { fmt, fmtDate } from '../../lib/constants';
import StatCard from '../../components/ui/StatCard';

export default function FinancialsPage() {
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [typeF,    setTypeF]    = useState('');
  const [monthly,  setMonthly]  = useState([]);
  const [byDist,   setByDist]   = useState({});

  const { contributions, summary, loading, refetch } = useContributions({ year, type: typeF || undefined });

  useEffect(() => {
    getMonthlyBreakdown(year).then(setMonthly);
    getDistrictBreakdown(year).then(setByDist);
  }, [year]);

  const maxMonth = Math.max(...monthly.map(m => m.total), 1);
  const maxDist  = Math.max(...Object.values(byDist), 1);

  async function handleDelete(id) {
    if (!window.confirm('Delete this contribution record?')) return;
    await deleteContribution(id);
    refetch();
  }

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{fontSize:20,fontWeight:800}}>💰 Financial Overview</h1>
          <div style={{fontSize:13,color:'var(--text3)',marginTop:2}}>Track all tithes, offerings and contributions</div>
        </div>
        <div className="flex gap-2">
          <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>
            {[2026,2025,2024,2023].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select className="form-select" style={{width:'auto'}} value={typeF} onChange={e=>setTypeF(e.target.value)}>
            <option value="">All Types</option>
            {['Tithe','Offering','Special','Harvest','Building Fund','Thanksgiving'].map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="grid-4 mb-4">
        <StatCard icon="💰" label="Grand Total"  value={fmt(summary.total||0)}    color="red" />
        <StatCard icon="🙏" label="Tithes"       value={fmt(summary.tithes||0)}   color="blue" />
        <StatCard icon="🎁" label="Offerings"    value={fmt(summary.offerings||0)} color="green" />
        <StatCard icon="⭐" label="Special/Other" value={fmt((summary.special||0)+(summary.building||0)+(summary.harvest||0))} color="amber" />
      </div>

      <div className="grid-2 mb-4">
        <div className="card">
          <div className="card-header"><h3>Monthly Trend {year}</h3></div>
          <div className="card-pad">
            <div className="bar-chart">
              {monthly.map(m=>(
                <div key={m.month} className="bar-row">
                  <span className="bar-label">{m.month}</span>
                  <div className="bar-track">
                    <div className="bar-fill blue" style={{width:`${Math.round((m.total/maxMonth)*100)}%`}}>
                      <span className="bar-fill-val">{m.total>0?Math.round(m.total/1000)+'k':''}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>By District</h3></div>
          <div className="card-pad">
            <div className="bar-chart">
              {Object.entries(byDist).map(([d,amt])=>(
                <div key={d} className="bar-row">
                  <span className="bar-label">{d}</span>
                  <div className="bar-track">
                    <div className="bar-fill red" style={{width:`${Math.round((amt/maxDist)*100)}%`}}>
                      <span className="bar-fill-val">{Math.round(amt/1000)}k</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading ? <div style={{textAlign:'center',padding:24,color:'var(--text3)'}}>Loading…</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Member</th><th>District</th><th>Type</th><th>Amount</th><th>Notes</th><th className="no-print">Del</th></tr></thead>
            <tbody>
              {contributions.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No contributions found for this period</td></tr>}
              {contributions.map(c=>(
                <tr key={c.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(c.date)}</td>
                  <td style={{fontWeight:700}}>{c.members?`${c.members.first_name} ${c.members.last_name}`:'—'}</td>
                  <td style={{fontSize:12}}>{c.members?.districts?.name||'—'}</td>
                  <td><span className={`badge badge-${c.type==='Tithe'?'red':c.type==='Offering'?'blue':'green'}`}>{c.type}</span></td>
                  <td style={{fontWeight:700,color:'var(--blue-dark)'}}>{fmt(c.amount)}</td>
                  <td style={{fontSize:12,color:'var(--text3)'}}>{c.notes||'—'}</td>
                  <td className="no-print"><button className="btn btn-danger btn-sm" onClick={()=>handleDelete(c.id)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
